import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import FieldHelpMark from '@/components/FieldHelpMark';

/**
 * TikTok 运营台查询区上方独立工具栏。
 * @param {React.ReactNode} [leading] 左侧插槽
 * @param {React.ReactNode} [trailing] 右侧插槽（如同步更新按钮，靠最右对齐）
 * @param {{
 *   key: string,
 *   label: string,
 *   hint?: string,
 *   onClick?: () => void,
 *   disabled?: boolean,
 *   tone?: 'default'|'primary'|'danger',
 *   menu?: {
 *     key: string,
 *     label: string,
 *     count?: number,
 *     onClick?: () => void,
 *     disabled?: boolean,
 *     tone?: 'default'|'danger',
 *     menu?: { key: string, label: string, onClick?: () => void, disabled?: boolean }[],
 *   }[],
 * }[]} actions
 */
export default function TiktokPageToolbar({
  actions = [],
  busy = false,
  leading = null,
  trailing = null,
}) {
  const hasActions = Array.isArray(actions) && actions.some((a) => a?.key && a?.label);
  if (!leading && !trailing && !hasActions) return null;

  return (
    <div className="tko-toolbar tko-toolbar--ops" role="toolbar" aria-label={fixedUi("TikTok 平台操作")}>
      {leading ? (
        <div className="tko-toolbar__leading">
          {leading}
        </div>
      ) : null}
      {hasActions ? (
        <div className="tko-toolbar__actions">
          {actions.map((action) => {
            if (!action?.key || !action?.label) return null;
            if (Array.isArray(action.menu) && action.menu.length > 0) {
              return (
                <ToolbarDropdown
                  key={action.key}
                  action={action}
                  busy={busy}
                  loading={Boolean(action.loading)}
                  loadingLabel={action.loadingLabel}
                />
              );
            }
            const tone = action.tone || 'default';
            const className = tone === 'primary'
              ? 'tko-btn tko-btn--primary tko-btn--sm'
              : tone === 'danger'
                ? 'tko-btn tko-btn--danger tko-btn--sm'
                : 'tko-btn tko-btn--secondary tko-btn--sm';
            // 问号叠在按钮内侧、文字左侧（与同步按钮一致；disabled 时仍可悬浮查看说明）
            if (action.hint) {
              return (
                <span key={action.key} className="tko-toolbar__action-with-hint">
                  <FieldHelpMark text={action.hint} className="tko-toolbar__action-hint" />
                  <OnceButton
                    type="button"
                    className={`${className} tko-toolbar__action-btn--with-hint`}
                    disabled={busy || Boolean(action.disabled)}
                    onClick={action.onClick}
                  >
                    {action.label}
                  </OnceButton>
                </span>
              );
            }
            return (
              <OnceButton
                key={action.key}
                type="button"
                className={className}
                disabled={busy || Boolean(action.disabled)}
                onClick={action.onClick}
              >
                {action.label}
              </OnceButton>
            );
          })}
        </div>
      ) : null}
      {trailing ? (
        <div className="tko-toolbar__trailing">
          {trailing}
        </div>
      ) : null}
    </div>
  );
}

function menuItemLabel(item) {
  const count = Number(item.count);
  if (Number.isFinite(count) && count >= 0) return `${item.label}（${count}）`;
  return item.label;
}

function ToolbarDropdown({ action, busy, loading = false, loadingLabel = '' }) {
  const [open, setOpen] = useState(false);
  const [openSubKey, setOpenSubKey] = useState(null);
  const rootRef = useRef(null);
  const tone = action.tone || 'default';
  const className = tone === 'primary'
    ? 'tko-btn tko-btn--primary tko-btn--sm'
    : tone === 'danger'
      ? 'tko-btn tko-btn--danger tko-btn--sm'
      : 'tko-btn tko-btn--secondary tko-btn--sm';
  const label = loading ? (loadingLabel || action.label) : action.label;

  useEffect(() => {
    if (!open) {
      setOpenSubKey(null);
      return undefined;
    }
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const closeAll = () => {
    setOpen(false);
    setOpenSubKey(null);
  };

  return (
    <div className={`tko-toolbar-dd${open ? ' is-open' : ''}${loading ? ' is-loading' : ''}`} ref={rootRef}>
      <OnceButton
        type="button"
        className={`${className} tko-toolbar-dd__trigger`}
        disabled={busy || Boolean(action.disabled)}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {loading ? (
          <>
            <i className="tko-spinner tko-spinner--sm" aria-hidden="true" />
            <span>{label}</span>
          </>
        ) : (
          <>
            <span>{label}</span>
            <ChevronDown size={14} strokeWidth={2.25} aria-hidden="true" />
          </>
        )}
      </OnceButton>
      {open ? (
        <div className="tko-toolbar-dd__menu" role="menu">
          {action.menu.map((item) => {
            if (!item?.key || !item?.label) return null;
            const hasSub = Array.isArray(item.menu) && item.menu.length > 0;
            if (hasSub) {
              const subOpen = openSubKey === item.key;
              return (
                <div
                  key={item.key}
                  className={`tko-toolbar-dd__item-wrap${subOpen ? ' is-open' : ''}`}
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="tko-toolbar-dd__item tko-toolbar-dd__item--has-sub"
                    disabled={busy || Boolean(item.disabled)}
                    aria-haspopup="menu"
                    aria-expanded={subOpen}
                    onClick={() => {
                      if (busy || item.disabled) return;
                      setOpenSubKey((prev) => (prev === item.key ? null : item.key));
                    }}
                  >
                    <span>{menuItemLabel(item)}</span>
                    <ChevronRight size={14} strokeWidth={2.25} aria-hidden="true" />
                  </button>
                  {subOpen ? (
                    <div className="tko-toolbar-dd__submenu" role="menu">
                      {item.menu.map((sub) => {
                        if (!sub?.key || !sub?.label) return null;
                        return (
                          <OnceButton
                            key={sub.key}
                            type="button"
                            role="menuitem"
                            className="tko-toolbar-dd__item"
                            disabled={busy || Boolean(sub.disabled)}
                            onClick={() => {
                              closeAll();
                              return sub.onClick?.();
                            }}
                          >
                            {menuItemLabel(sub)}
                          </OnceButton>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            }
            const itemTone = item.tone || 'default';
            return (
              <OnceButton
                key={item.key}
                type="button"
                role="menuitem"
                className={`tko-toolbar-dd__item${itemTone === 'danger' ? ' tko-toolbar-dd__item--danger' : ''}`}
                disabled={busy || Boolean(item.disabled)}
                onClick={() => {
                  closeAll();
                  return item.onClick?.();
                }}
              >
                {menuItemLabel(item)}
              </OnceButton>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function OnceButton({ disabled = false, onClick, children, ...rest }) {
  const lockedRef = useRef(false);
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;
  return (
    <button
      disabled={disabled}
      onClick={async (event) => {
        if (disabled || lockedRef.current) return;
        lockedRef.current = true;
        try {
          await onClickRef.current?.(event);
        } finally {
          lockedRef.current = false;
        }
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

/** 简易 CSV 导出（UTF-8 BOM） */
export function downloadCsv(filename, headers, rows) {
  const escape = (value) => {
    const text = value == null ? '' : String(value);
    if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
  };
  const lines = [headers.join(',')];
  for (const row of rows) {
    lines.push(headers.map((key) => escape(row?.[key])).join(','));
  }
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
